import { type SortDirection } from '@tanstack/react-table';
import classNames from 'classnames';
import { forwardRef, type HTMLAttributes, memo } from 'react';

import { ArrowDown as ArrowDownIcon } from '@air/next-icons';
import { VisuallyHidden } from '@air/primitive-visually-hidden';

import { SORT_BUTTON, SORT_BUTTON_ARROW, SORT_BUTTON_TYPE } from '~/constants/testIDs';

const BUTTON_CLASS_NAMES =
  'group flex h-8 cursor-pointer items-center justify-start gap-1 border-0 bg-transparent py-0 text-12 font-semibold transition-all hover:underline';

export type SortButtonProps = Pick<HTMLAttributes<HTMLDivElement>, 'children' | 'className' | 'style'> & {
  direction: SortDirection;
  isActive: boolean;
  onClick?: () => void;
  type?: string;
};

export const _SortButton = forwardRef<HTMLDivElement, SortButtonProps>(
  ({ children, className, direction, onClick, style, type, isActive }: SortButtonProps, forwardedRef) => (
    <div
      data-testid={SORT_BUTTON}
      data-direction={direction}
      className={classNames('inline-flex', className)}
      ref={forwardedRef}
      style={style}
    >
      <button
        className={classNames(BUTTON_CLASS_NAMES, direction ? 'text-grey-11' : 'text-grey-10', isActive && 'underline')}
        data-testid={`${SORT_BUTTON_TYPE}-${type}`}
        data-direction={direction}
        onClick={onClick}
        type="button"
      >
        <div className="truncate">{children}</div>
        <ArrowDownIcon
          className={classNames('h-3 min-w-3 max-w-3 transition-all', {
            'rotate-180': direction === 'asc',
            'rotate-0': direction === 'desc',
            'opacity-0': !isActive,
            'opacity-100': isActive,
            'group-hover:opacity-100': !isActive,
          })}
          data-testid={`${SORT_BUTTON_ARROW}-${type}-${direction}`}
          data-direction={direction}
        />
      </button>
      <VisuallyHidden>{direction === 'desc' ? 'Sort descending' : 'Sort ascending'}</VisuallyHidden>
    </div>
  ),
);

_SortButton.displayName = 'SortButton';

export const SortButton = memo(_SortButton);
